/**
 * Read a GET endpoint, painting what this tab last saw while it checks.
 *
 * The app has no router: an area is `current === 'x' && <X />`, so switching
 * tabs UNMOUNTS the one you left and mounts the one you arrived at from
 * nothing. Every mount effect runs again — `Workspace` alone fires 32 `api()`
 * calls — and each one waits on a request whose serial hops (edge, session
 * lookup, proxy, policy read, query) are what "loading between screens"
 * actually is. The ETag work already in `api.ts` saved the bytes on the way
 * back; it never saved the wait, because a 304 still pays every hop.
 *
 * So the wait stops being in front of the screen. `peek()` returns the last
 * body this tab saw at this path, the first frame renders it, and the request
 * goes out behind it. A revisit is instant and then correct. A first visit is
 * exactly what it was before.
 *
 * Deliberately not a cache library. There is one consumer shape here — a GET
 * whose answer a screen renders — and the store it reads is the one `api.ts`
 * already keeps. Adding TanStack Query for this would be a dependency, a
 * provider and a second cache with its own eviction rules, sitting on top of
 * the cache that would still be underneath it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { api, peek } from './api';

export interface Query<T> {
  /** The body: cached on a revisit, fresh once the request lands. */
  data: T | undefined;
  /** The last error, cleared by the next success. `data` may be set alongside it. */
  error: string | null;
  /**
   * True only when there is nothing to show — a genuine first visit.
   *
   * A revalidation behind a painted screen is NOT loading. Reporting it as
   * loading would put the spinner back exactly where this hook exists to
   * remove it, on every tab switch, over content already on screen.
   */
  loading: boolean;
  /** True while a request is in flight, painted or not. For a quiet indicator. */
  validating: boolean;
  /** Re-read now — after a mutation, or from a retry control. */
  reload: () => void;
}

export interface QueryOptions {
  /**
   * Skip the request and report nothing. For a query whose inputs are not ready
   * — a drawer with no account selected yet.
   */
  enabled?: boolean;
  /**
   * Re-read on a timer, in milliseconds. Cleared on unmount, and skipped while
   * the tab is hidden — every hand-rolled poll in this app already checked
   * `document.hidden`, and a background tab polling a Worker for hours is the
   * kind of cost nobody attributes to the tab they forgot about.
   */
  refreshMs?: number;
  /**
   * Wait this long after `path` changes before asking. For search boxes, whose
   * path changes on every keystroke.
   */
  debounceMs?: number;
}

export function useQuery<T>(path: string | null, options: QueryOptions = {}): Query<T> {
  const { enabled = true, refreshMs, debounceMs = 0 } = options;
  const active = enabled && path !== null;

  const [data, setData] = useState<T | undefined>(() => (active ? peek<T>(path) : undefined));
  const [error, setError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [nonce, setNonce] = useState(0);

  /*
   * Which path the state on screen belongs to.
   *
   * Two requests can be in flight at once — a filter changed while the previous
   * answer was still travelling — and they can land in either order. Without
   * this, the slower of the two wins and the screen shows the result of a
   * filter the user has already moved off. Compared at resolution rather than
   * cancelled, because `api()` returns a promise and not an AbortController,
   * and a late answer that is simply ignored costs nothing.
   */
  const wanted = useRef<string | null>(null);

  /**
   * Which path the body on screen was painted for.
   *
   * Seeded with the mount path, because `useState`'s initialiser above has
   * already done that paint — without the seed the first effect would repaint
   * the same value and cost a render for nothing.
   */
  const painted = useRef<string | null>(active ? path : null);

  const run = useCallback(
    async (target: string) => {
      wanted.current = target;
      setValidating(true);
      try {
        const body = await api<T>(target);
        if (wanted.current !== target) return;
        setData(body);
        setError(null);
      } catch (e) {
        if (wanted.current !== target) return;
        /*
         * The stale body is deliberately LEFT on screen beside the error.
         *
         * A failed revalidation does not make what is already drawn untrue — it
         * makes it old. Blanking the screen to report that would lose a rep
         * their place over a dropped request, and the error is rendered
         * alongside it, so nobody is being told the data is current.
         */
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (wanted.current === target) setValidating(false);
      }
    },
    [],
  );

  useEffect(() => {
    if (!active) {
      wanted.current = null;
      return;
    }

    /*
     * Paint this path's cached body when the path CHANGES, and only then.
     *
     * `useState`'s initialiser only runs on mount, so without this a filter or
     * a page change would hold the previous path's rows on screen until the new
     * ones arrived — a worse lie than a blank, because those rows look like an
     * answer to the question just asked.
     *
     * Guarded on the path rather than run every time, because `reload()` also
     * lands here: re-reading the cache after a mutation would flick the screen
     * back to the body from BEFORE the mutation for as long as the re-read
     * takes, which looks exactly like the write having been rejected.
     */
    if (painted.current !== path) {
      painted.current = path;
      setData(peek<T>(path));
    }

    if (debounceMs > 0) {
      const timer = setTimeout(() => void run(path), debounceMs);
      return () => clearTimeout(timer);
    }
    void run(path);
    return undefined;
  }, [active, path, debounceMs, run, nonce]);

  useEffect(() => {
    if (!active || !refreshMs) return undefined;
    const timer = setInterval(() => {
      if (document.hidden) return;
      void run(path);
    }, refreshMs);
    return () => clearInterval(timer);
  }, [active, path, refreshMs, run]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return {
    data,
    error,
    loading: active && data === undefined && error === null,
    validating,
    reload,
  };
}
